/**
 * Utility functions for detecting and locating elements in the questionnaire editor
 */
import { parseQuestionnaireStructure, StructureNode, NodeType } from './structureParser'

/**
 * Represents an editor element with pixel boundaries for rendering
 */
export interface EditorElement {
  type: NodeType
  name: string
  lineNumber: number
  endLineNumber: number
  metadata?: StructureNode['metadata']
  // Pixel boundaries for rendering highlight
  startY: number
  endY: number
  height: number
  // Raw content lines for additional analysis
  contentLines?: string[]
}

/**
 * Get the line number from a mouse Y position in the editor
 */
export const getLineNumberFromMousePosition = (
  mouseY: number,
  editorContainer: HTMLElement
): number => {
  const editorContent = editorContainer.querySelector('.ProseMirror')
  if (!editorContent) return -1

  const lines = editorContent.querySelectorAll('.ProseMirror > *') as NodeListOf<HTMLElement>
  const containerRect = editorContainer.getBoundingClientRect()
  const scrollTop = editorContainer.scrollTop

  // Adjust mouse Y for scroll position
  const adjustedY = mouseY - containerRect.top + scrollTop

  let lineNumber = 1
  let accumulatedHeight = 0

  for (let i = 0; i < lines.length; i++) {
    const lineHeight = lines[i].offsetHeight
    if (adjustedY >= accumulatedHeight && adjustedY < accumulatedHeight + lineHeight) {
      lineNumber = i + 1
      break
    }
    accumulatedHeight += lineHeight
    if (i === lines.length - 1 && adjustedY >= accumulatedHeight) {
      lineNumber = i + 1
    }
  }

  return lineNumber
}

/**
 * Calculate pixel boundaries for a line range in the editor
 */
export const calculatePixelBoundaries = (
  startLine: number,
  endLine: number,
  editorContainer: HTMLElement
): { startY: number; endY: number; height: number } | null => {
  const editorContent = editorContainer.querySelector('.ProseMirror')
  if (!editorContent) return null

  const lines = editorContent.querySelectorAll('.ProseMirror > *') as NodeListOf<HTMLElement>
  if (startLine < 1 || startLine > lines.length) return null

  const startLineElement = lines[startLine - 1]
  const endLineElement = lines[Math.min(endLine - 1, lines.length - 1)]

  if (!startLineElement || !endLineElement) return null

  const startY = startLineElement.offsetTop
  const endY = endLineElement.offsetTop + endLineElement.offsetHeight
  const height = endY - startY

  return { startY, endY, height }
}

/**
 * Find the structure node that contains the given line number
 */
export const findNodeAtLine = (
  node: StructureNode,
  lineNumber: number
): StructureNode | null => {
  // Check if line is within this node's range first
  if (
    lineNumber >= node.lineNumber &&
    lineNumber <= (node.endLineNumber || node.lineNumber)
  ) {
    // For EXCEPTIONS lists, always return the EXCEPTIONS node, not child items
    if (node.type === NodeType.EXCEPTIONS) {
      return node
    }

    // For other nodes, check children first to find most specific match
    if (node.children && node.children.length > 0) {
      for (const child of node.children) {
        const childMatch = findNodeAtLine(child, lineNumber)
        if (childMatch) return childMatch
      }
    }

    // Return this node if it's not the root Survey
    if (node.type !== NodeType.SURVEY) {
      return node
    }
  }

  // If line is not in this node's range, check children
  if (node.children && node.children.length > 0) {
    for (const child of node.children) {
      const childMatch = findNodeAtLine(child, lineNumber)
      if (childMatch) return childMatch
    }
  }

  return null
}

/**
 * Detect the editor element at a specific line number
 */
export const detectElementAtLine = (
  lineNumber: number,
  editorContent: string,
  editorContainer: HTMLElement
): EditorElement | null => {
  // Parse structure
  const structure = parseQuestionnaireStructure(editorContent)

  // Find node at line
  const node = findNodeAtLine(structure, lineNumber)

  if (!node || node.type === NodeType.SURVEY) {
    return null // Don't highlight root
  }

  // Calculate pixel boundaries
  const boundaries = calculatePixelBoundaries(
    node.lineNumber,
    node.endLineNumber || node.lineNumber,
    editorContainer
  )

  if (!boundaries) return null

  // Get content lines for additional analysis
  const lines = editorContent.split('\n')
  const contentLines = lines.slice(node.lineNumber - 1, node.endLineNumber || node.lineNumber)

  return {
    type: node.type,
    name: node.name,
    lineNumber: node.lineNumber,
    endLineNumber: node.endLineNumber || node.lineNumber,
    metadata: node.metadata,
    contentLines,
    ...boundaries,
  }
}

/**
 * Check if a question is a GRID question (has PER keyword)
 */
export const isGridQuestion = (element: EditorElement): boolean => {
  if (element.type !== NodeType.QUESTION || !element.contentLines) return false
  return element.contentLines.some(line => line.trim().match(/^PER\b/i))
}

/**
 * Check if a question has a FROM list reference
 */
export const hasFromList = (element: EditorElement): boolean => {
  if (element.type !== NodeType.QUESTION || !element.contentLines) return false
  return element.contentLines.some(line => line.trim().match(/^SELECT\s+(ONE|MULTIPLE)\s+FROM\b/i))
}

/**
 * Check if a question has a PER list reference
 */
export const hasPerList = (element: EditorElement): boolean => {
  if (element.type !== NodeType.QUESTION || !element.contentLines) return false
  return element.contentLines.some(line => line.trim().match(/^PER\b/i))
}

/**
 * Get the question type based on its content
 * Returns: "Single Select", "Multiple Select", "Grid Single Select", "Grid Multiple Select", "Fill In Number", "Fill In Text", or "Question"
 */
export const getQuestionType = (element: EditorElement): string => {
  if (element.type !== NodeType.QUESTION || !element.contentLines) return 'Question'

  const contentText = element.contentLines.join('\n')
  const hasPER = element.contentLines.some(line => line.trim().match(/^PER\b/i))

  // Check for FILL IN types first
  if (contentText.match(/FILL\s+IN\s+NUMBER/i)) {
    return 'Fill In Number'
  }
  if (contentText.match(/FILL\s+IN\s+TEXT/i)) {
    return 'Fill In Text'
  }

  // Check for SELECT types
  const hasSelectOne = contentText.match(/SELECT\s+ONE\s+FROM/i)
  const hasSelectMultiple = contentText.match(/SELECT\s+MULTIPLE\s+FROM/i)

  if (hasPER) {
    // Grid questions
    if (hasSelectOne) {
      return 'Grid Single Select'
    }
    if (hasSelectMultiple) {
      return 'Grid Multiple Select'
    }
    return 'Grid Question'
  } else {
    // Non-grid questions
    if (hasSelectOne) {
      return 'Single Select'
    }
    if (hasSelectMultiple) {
      return 'Multiple Select'
    }
  }

  return 'Question'
}

/**
 * Delete an element from the editor content
 */
export const deleteElement = (
  element: EditorElement,
  currentContent: string
): string => {
  const lines = currentContent.split('\n')
  const newLines = [
    ...lines.slice(0, element.lineNumber - 1),
    ...lines.slice(element.endLineNumber),
  ]
  return newLines.join('\n')
}

/**
 * Duplicate an element in the editor content
 */
export const duplicateElement = (
  element: EditorElement,
  currentContent: string
): string => {
  const lines = currentContent.split('\n')
  const elementLines = lines.slice(element.lineNumber - 1, element.endLineNumber)

  // Modify duplicated element based on type
  const modifiedLines = elementLines.map((line, index) => {
    if (index === 0) {
      // Modify the first line to indicate it's a copy
      if (element.type === NodeType.QUESTION) {
        return line.replace(/^(Q\s+)(.+?):/, `$1$2_copy:`)
      } else if (element.type === NodeType.SECTION) {
        return line.replace(/^(SECTION\s+)(.+)/, `$1$2 (Copy)`)
      } else if (element.type === NodeType.LIST || element.type === NodeType.RANGE_LIST) {
        return line.replace(/^((?:RANGE\s+)?LIST\s+)(.+?)(\s*:.*)/, `$1$2_copy$3`)
      } else if (element.type === NodeType.TEXT || element.type === NodeType.NOTE) {
        return line.replace(/^((?:TEXT|NOTE)\s+)(.+?):/, `$1$2_copy:`)
      }
    }
    return line
  })

  // Insert duplicated element after the original
  const newLines = [
    ...lines.slice(0, element.endLineNumber),
    '', // Empty line for separation
    ...modifiedLines,
    ...lines.slice(element.endLineNumber),
  ]

  return newLines.join('\n')
}

/**
 * Insert text at a specific line number
 */
export const insertTextAtLine = (
  lineNumber: number,
  textToInsert: string,
  currentContent: string,
  position: 'before' | 'after' | 'append' = 'append'
): string => {
  const lines = currentContent.split('\n')

  if (position === 'append' && lineNumber > 0 && lineNumber <= lines.length) {
    // Append to the end of the line
    lines[lineNumber - 1] += textToInsert
  } else if (position === 'before' && lineNumber > 0) {
    lines.splice(lineNumber - 1, 0, textToInsert)
  } else if (position === 'after' && lineNumber > 0) {
    lines.splice(lineNumber, 0, textToInsert)
  }

  return lines.join('\n')
}
